<script lang="ts" setup>
import { computed } from "vue";
import { useStore } from "vuex";

const store = useStore();

const hasDocumentSpaces = computed(() =>
  store.getters.user.hasDocumentSpaces(),
);
const canUseSharedSpaces = computed(() =>
  store.getters.user.canUseSharedSpaces(),
);

const hasAnalytics = computed(() => store.getters.user.hasAnalytics());
const hasDocumentAnalytics = computed(() =>
  store.getters.user.hasDocumentAnalytics(),
);
const hasUsageAnalytics = computed(() =>
  store.getters.user.hasUsageAnalytics(),
);
const hasPresentationAnalytics = computed(() =>
  store.getters.user.hasPresentationAnalytics(),
);
const hasDocumentTracking = computed(() =>
  store.getters.user.hasDocumentTracking(),
);
</script>

<template>
  <div class="pane page_content" data-test="classic-analytics-overview">
    <div class="title_container">
      <h3>{{ $t("analyticsOverview.title") }}</h3>
    </div>

    <div class="nav_list">
      <div
        class="group"
        v-if="
          canUseSharedSpaces &&
          ((hasAnalytics && hasDocumentSpaces) || hasDocumentAnalytics)
        "
      >
        <h4>{{ $t("analyticsOverview.sharedSpaces.title") }}</h4>

        <router-link
          v-if="canUseSharedSpaces && hasAnalytics && hasDocumentSpaces"
          class="nav_item"
          :to="{ name: 'Dashboard' }"
        >
          <span class="material-symbols-outlined nav_icon">visibility</span>
          <div>
            <span class="nav_label">
              {{ $t("analyticsOverview.sharedSpaces.visits.linkText") }}
            </span>
            <span class="nav_desc">
              {{ $t("analyticsOverview.sharedSpaces.visits.info") }}
            </span>
          </div>
          <span class="material-symbols-outlined arrow">chevron_right</span>
        </router-link>

        <router-link
          v-if="canUseSharedSpaces && hasDocumentAnalytics"
          class="nav_item"
          :to="{ name: 'DocumentAnalytics' }"
        >
          <span class="material-symbols-outlined nav_icon">description</span>
          <div>
            <span class="nav_label">
              {{ $t("analyticsOverview.sharedSpaces.documents.linkText") }}
            </span>
            <span class="nav_desc">
              {{ $t("analyticsOverview.sharedSpaces.documents.info") }}
            </span>
          </div>
          <span class="material-symbols-outlined arrow">chevron_right</span>
        </router-link>
      </div>

      <router-link
        v-if="hasUsageAnalytics"
        class="nav_item"
        :to="{ name: 'UsageAnalytics' }"
      >
        <span class="material-symbols-outlined nav_icon">bar_chart</span>
        <div>
          <span class="nav_label">
            {{ $t("analyticsOverview.usageAnalytics.linkText") }}
          </span>
          <span class="nav_desc">
            {{ $t("analyticsOverview.usageAnalytics.info") }}
          </span>
        </div>
        <span class="material-symbols-outlined arrow">chevron_right</span>
      </router-link>

      <router-link
        v-if="hasPresentationAnalytics"
        class="nav_item"
        :to="{ name: 'PresentationAnalyticsSelector' }"
        data-test="presentation-analytics-overview-link"
      >
        <span class="material-symbols-outlined nav_icon">monitoring</span>
        <div>
          <span class="nav_label">
            {{ $t("analyticsOverview.presentationAnalytics.linkText") }}
          </span>
          <span class="nav_desc">
            {{ $t("analyticsOverview.presentationAnalytics.info") }}
          </span>
        </div>
        <span class="material-symbols-outlined arrow">chevron_right</span>
      </router-link>

      <router-link
        v-if="hasDocumentTracking"
        class="nav_item"
        :to="{ name: 'DocumentHistory' }"
      >
        <span class="material-symbols-outlined nav_icon">history</span>
        <div>
          <span class="nav_label">
            {{ $t("analyticsOverview.presentationHistory.linkText") }}
          </span>
          <span class="nav_desc">
            {{ $t("analyticsOverview.presentationHistory.info") }}
          </span>
        </div>
        <span class="material-symbols-outlined arrow">chevron_right</span>
      </router-link>
    </div>
  </div>
</template>

<style scoped>
.pane {
  font-size: 0.9rem;
}

.nav_list {
  max-width: 600px;
}

.group {
  margin-bottom: 1.2em;
  padding-bottom: 0.8em;
  border-bottom: 1px solid var(--classic-border-subtle, #e8e8e8);
}

.group h4 {
  font-weight: 500;
  font-size: 1em;
  color: var(--classic-text, #363636);
  margin: 0 0 0.4em 0;
}

.nav_item {
  display: flex;
  align-items: flex-start;
  gap: 0.9em;
  padding: 0.8em 0.9em;
  border-radius: 0.4em;
  text-decoration: none;
  color: inherit;
  transition: background-color 0.15s;
}

.nav_item:hover {
  background-color: var(--classic-surface-hover, #f5f5f5);
}

@media (prefers-color-scheme: dark) {
  :global(
    .classic-theme
      [data-test="classic-analytics-overview"]
      .nav_item:focus-visible
  ) {
    background-color: var(--classic-surface-hover);
    outline: 2px solid var(--classic-focus);
    outline-offset: 2px;
  }
}

.nav_icon {
  font-size: 1.4em;
  color: var(--classic-text-secondary, #666);
  flex-shrink: 0;
  margin-top: 0.1em;
}

.nav_item > div {
  flex: 1;
  min-width: 0;
}

.nav_label {
  display: block;
  font-weight: 500;
  color: var(--classic-text, #363636);
}

.nav_desc {
  display: block;
  font-size: 0.88em;
  color: var(--classic-text-muted, #888);
  margin-top: 0.15em;
  line-height: 1.4;
}

.arrow {
  margin-left: auto;
  font-size: 1.3em;
  color: var(--classic-border-strong, #bbb);
  flex-shrink: 0;
  margin-top: 0.1em;
}
</style>
